import { BankAccountType, useBankActivity, useWealthOverview } from '@egw/api'
import { Link } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useNavigate } from 'react-router'
import { TransactionItem } from '../../components/transaction-item/transaction-item'

export interface RecentActivityProps {
  alias: number
}

export const RecentActivity: FC<RecentActivityProps> = ({ alias }) => {
  const { t } = useTranslation('features-transactions')
  const navigate = useNavigate()

  const { data: wealthOverviewData } = useWealthOverview()

  const accounts = [
    ...(wealthOverviewData?.highYieldChecking?.accounts ?? []),
    ...(wealthOverviewData?.highYieldSavings?.accounts ?? []),
  ]

  const account = accounts.find((curr) => curr.alias === alias)

  const { data, isLoading, error } = useBankActivity({ alias })

  return (
    <div className="egw-container">
      {isLoading ? (
        <div>
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {data?.activity.length && account ? (
        <>
          <div className="flex gap-2 mb-6 items-baseline">
            <h3 className="typo-primary-xl-light text-text-header flex-1 text-header leading-8 md:leading-10">
              {t('recent_activity.title')}
            </h3>
            {account.accountType === BankAccountType.HIGH_YIELD_CHECKING && (
              <Link
                className="typo-primary-sm-semibold text-text-action"
                href={WEALTH_ROUTES.CHECKING_ACCOUNT_ACTIVITY.path.replace(':alias', alias.toString())}
                testId="recent-activity-all-activity-link"
              >
                {t('recent_activity.all_activity')}
              </Link>
            )}
            {account.accountType === BankAccountType.HIGH_YIELD_SAVINGS && (
              <Link href={WEALTH_ROUTES.SAVINGS_ACCOUNT_ACTIVITY.path.replace(':alias', alias.toString())}>
                {t('recent_activity.all_activity')}
              </Link>
            )}
          </div>
          <div>
            {data.activity.slice(0, 5).map((transaction) => (
              <TransactionItem
                key={transaction.activityId}
                transaction={transaction}
                onClick={() => {
                  account.accountType === BankAccountType.HIGH_YIELD_CHECKING &&
                    void navigate(
                      WEALTH_ROUTES.CHECKING_ACCOUNT_ACTIVITY_DETAILS.path
                        .replace(':alias', alias.toString())
                        .replace(':transactionId', transaction.activityId.toString()),
                    )
                  account.accountType === BankAccountType.HIGH_YIELD_SAVINGS &&
                    void navigate(
                      WEALTH_ROUTES.SAVINGS_ACCOUNT_ACTIVITY_DETAILS.path
                        .replace(':alias', alias.toString())
                        .replace(':transactionId', transaction.activityId.toString()),
                    )
                }}
              />
            ))}
          </div>
        </>
      ) : null}

      {data?.activity.length === 0 && account ? (
        <div className="flex flex-row gap-2xs">
          <div className="rounded-full p-2xs bg-background-light h-fit flex items-center justify-center mr-3xs">
            <ClipboardDocumentListIcon className="w-7 h-7 text-text-icons-full flex-shrink-0 self-start" />
          </div>
          <div className="flex flex-col justify-center">
            <p className="text-text-header typo-primary-base-semibold ">{t('recent_activity.no_recent_activity')}</p>
          </div>
        </div>
      ) : null}

      {error || (!account && !isLoading) ? (
        <>
          <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
          <p className="text-error text-2xl font-light mb-10">{t('common.generic_error')}</p>
          <p className="text-body text-xl">{t('activity.error')}</p>
        </>
      ) : null}
    </div>
  )
}
